import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import AddCustomSearchEnginePopup from "@/components/add-custom-search-engine-popup";
import PopupShell from "@/components/popup-shell";
import styles from "@/components/search-engine-popup.module.css";
import useSearchEngines from "@/hooks/use-search-engines";
import * as signals from "@/signals";
import { DialogIcon } from "@/types";
import {
	closePopup,
	handleKeyboardClick,
	logIn,
	showDialog,
	showMenu,
	updateSelectedSearchEngine,
} from "@/utils";
import { faCircleCheck, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, MouseEvent, useState } from "react";

interface SearchEnginePopupProps {
	isShown: boolean;
}

function SearchEnginePopup({ isShown }: SearchEnginePopupProps): JSX.Element {
	const queryClient = useQueryClient();

	const { searchEngines } = useSearchEngines();

	const [
		isAddCustomSearchEnginePopupShown,
		setIsAddCustomSearchEnginePopupShown,
	] = useState<boolean>(false);

	const closeSearchEnginePopup = (): void => {
		closePopup("searchEngine");
	};

	const openAddCustomSearchEnginePopup = (): void => {
		if (!signals.login.value.username) {
			logIn();
			return;
		}
		setIsAddCustomSearchEnginePopupShown(true);
	};

	const engineListItems = searchEngines.map((engine) => {
		const isSelected = signals.selectedSearchEngine.value.id === engine.id;

		const handleClick = (): void => {
			updateSelectedSearchEngine({
				currentSearchEngineId: signals.selectedSearchEngine.value.id,
				newSearchEngine: engine,
			});
		};

		const handleContextMenu = (event: MouseEvent<HTMLDivElement>): void => {
			const isCustomEngine = !engine.id.startsWith("preset-");
			if (!isCustomEngine) {
				return;
			}

			showMenu(event, [
				{
					content: {
						icon: faTrashCan,
						isDangerous: true,
						text: t("delete"),
					},
					onClick: async (): Promise<void> => {
						await showDialog(t("confirmDeleteThisSearchEngine"), {
							icon: DialogIcon.WARNING,
							showCancel: true,
							title: t("confirm"),
						});
						signals.isLoadingShown.value = true;
						try {
							await new QueryDataManager(
								queryClient,
							).deleteSearchEngine(engine.id);
							signals.selectedSearchEngine.value =
								searchEngines[0];
						} catch (error) {
							void api.handleApiError(error);
						} finally {
							signals.isLoadingShown.value = false;
						}
					},
					when: true,
				},
			]);
		};

		return (
			<div
				aria-checked={isSelected}
				aria-label={engine.name}
				className={styles["list-item"]}
				key={engine.id}
				role="radio"
				onClick={handleClick}
				onContextMenu={handleContextMenu}
				onKeyDown={handleKeyboardClick(handleClick)}
				tabIndex={0}
				title={engine.name + " - " + engine.url}
			>
				<div className={styles["engine-info-container"]}>
					<i>{engine.icon}</i>
					<div className={styles["engine-info-text-container"]}>
						<div className={styles["engine-name"]}>
							{engine.name}
						</div>
						<div className={styles["engine-url"]}>{engine.url}</div>
					</div>
				</div>
				{isSelected && <FontAwesomeIcon icon={faCircleCheck} />}
			</div>
		);
	});

	return (
		<>
			<PopupShell
				isShown={isShown}
				onClose={closeSearchEnginePopup}
				title={t("searchEnginePreferences")}
			>
				<div className={styles["list"]}>
					{engineListItems}
					<div
						className={clsx(
							styles["list-item"],
							styles["add-button"],
						)}
						onClick={openAddCustomSearchEnginePopup}
						onKeyDown={handleKeyboardClick(
							openAddCustomSearchEnginePopup,
						)}
						role="button"
						tabIndex={0}
						title={t("addCustomSearchEngine")}
					>
						<i>&#xe7dc;</i>
						{t("customSearchEngine")}
					</div>
				</div>
			</PopupShell>
			<AddCustomSearchEnginePopup
				isShown={isAddCustomSearchEnginePopupShown}
				setIsShown={setIsAddCustomSearchEnginePopupShown}
			/>
		</>
	);
}

export default SearchEnginePopup;
